import { useEffect, useRef, useCallback, useState } from "react";
import QRCode from "qrcode";
import { Button } from "@/components/ui/button";
import { Printer, QrCode, RefreshCw } from "lucide-react";
import { useRotatingQrToken, type QrType } from "@/hooks/useRotatingQrToken";
import { escapeHtml } from "@/lib/escapeHtml";

interface QREntry {
  label: string;
  qrType: QrType;
  staticValue: string | null;
  color: string;
}

interface StudentBarcodesProps {
  studentName: string;
  barcodeAdmin: string | null;
  barcodeAcademic: string | null;
  barcodeExamination: string | null;
  barcodeAccounting: string | null;
  /**
   * When true, render rotating short-lived QR tokens fetched from the
   * `mint-student-qr` edge function instead of the static printed values.
   * Use this on the live student dashboard. Leave false for staff print/issue views.
   */
  rotating?: boolean;
}

function StaticQRCard({ label, value, color }: { label: string; value: string | null; color: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (canvasRef.current && value) {
      const isMobile = window.innerWidth < 640;
      QRCode.toCanvas(canvasRef.current, value, {
        width: isMobile ? 100 : 120,
        margin: 1,
        color: { dark: "#000000", light: "#ffffff" },
      });
    }
  }, [value]);

  if (!value) {
    return (
      <div className="text-center space-y-1 p-3 rounded-lg border bg-muted/30">
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
        <div className="h-[100px] sm:h-[120px] flex items-center justify-center text-xs text-muted-foreground">N/A</div>
      </div>
    );
  }

  return (
    <div className="text-center space-y-1 p-3 rounded-lg border bg-background">
      <p className={`text-[10px] font-semibold uppercase tracking-wider ${color}`}>{label}</p>
      <canvas ref={canvasRef} className="mx-auto" />
      <p className="text-[9px] text-muted-foreground font-mono">{value}</p>
    </div>
  );
}

function RotatingQRCard({ label, qrType, color, studentName }: { label: string; qrType: QrType; color: string; studentName: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { token, expiresAt, ttlMs, loading, error, refresh } = useRotatingQrToken(qrType);
  const [now, setNow] = useState(() => Date.now());
  const [fadeTick, setFadeTick] = useState(0);

  // Draw QR whenever token changes. Don't remount the canvas — just redraw.
  useEffect(() => {
    if (!canvasRef.current || !token) return;
    const isMobile = window.innerWidth < 640;
    QRCode.toCanvas(canvasRef.current, token, {
      width: isMobile ? 100 : 120,
      margin: 1,
      color: { dark: "#000000", light: "#ffffff" },
    }).then(() => {
      setFadeTick((k) => k + 1);
    }).catch(() => { /* ignore */ });
  }, [token]);

  // Tick once a second — countdown only, no network
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const remaining = expiresAt ? Math.max(0, expiresAt - now) : 0;
  const pct = expiresAt && ttlMs ? Math.max(0, Math.min(100, (remaining / ttlMs) * 100)) : 0;
  const secondsLeft = Math.ceil(remaining / 1000);

  return (
    <div className="text-center space-y-1 p-3 rounded-lg border bg-background">
      <p className={`text-[10px] font-semibold uppercase tracking-wider ${color}`}>{label}</p>
      <div className="relative mx-auto" style={{ width: "fit-content" }}>
        {token ? (
          <canvas
            ref={canvasRef}
            className="mx-auto transition-opacity duration-300"
            style={{ opacity: fadeTick > 0 ? 1 : 0 }}
          />
        ) : (
          <div className="h-[100px] sm:h-[120px] w-[100px] sm:w-[120px] flex items-center justify-center text-[10px] text-muted-foreground">
            {loading ? "Loading…" : error ? "Tap refresh" : ""}
          </div>
        )}
        {/* Watermark overlay — semi-transparent, rotated, hard to crop */}
        {token && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
          >
            <span
              className="text-[8px] font-semibold text-foreground/30 -rotate-12 select-none whitespace-nowrap"
              style={{ textShadow: "0 0 2px rgba(255,255,255,0.6)" }}
            >
              {studentName} · {new Date(now).toLocaleTimeString()}
            </span>
          </div>
        )}
      </div>

      {/* Countdown bar */}
      <div className="h-1 w-full bg-muted rounded overflow-hidden">
        <div
          className="h-full bg-primary transition-[width] duration-1000 ease-linear"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex items-center justify-center gap-1 text-[9px] text-muted-foreground">
        <RefreshCw className="h-2.5 w-2.5" />
        Refreshes in {secondsLeft}s
        <button
          type="button"
          onClick={refresh}
          className="underline ml-1"
          disabled={loading}
        >
          Sync
        </button>
      </div>
    </div>
  );
}

export function StudentBarcodes({
  studentName,
  barcodeAdmin,
  barcodeAcademic,
  barcodeExamination,
  barcodeAccounting,
  rotating = false,
}: StudentBarcodesProps) {
  const printRef = useRef<HTMLDivElement>(null);

  const entries: QREntry[] = [
    { label: "Administration", qrType: "admin", staticValue: barcodeAdmin, color: "text-blue-600" },
    { label: "Academic", qrType: "academic", staticValue: barcodeAcademic, color: "text-green-600" },
    { label: "Examination", qrType: "examination", staticValue: barcodeExamination, color: "text-orange-600" },
    { label: "Accounting", qrType: "accounting", staticValue: barcodeAccounting, color: "text-purple-600" },
  ];

  const handlePrint = useCallback(async () => {
    // Print always uses the static issued codes — printing a rotating token is meaningless
    const urls: Record<string, string> = {};
    for (const e of entries) {
      if (e.staticValue) {
        urls[e.label] = await QRCode.toDataURL(e.staticValue, { width: 160, margin: 1 });
      }
    }

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const cards = entries
      .map((e) => {
        const imgSrc = urls[e.label];
        if (!imgSrc) return "";
        return `<div class="card"><p>${escapeHtml(e.label)}</p><img src="${imgSrc}" /><span>${escapeHtml(e.staticValue)}</span></div>`;
      })
      .join("");

    printWindow.document.write(`
      <html><head><title>QR Codes - ${escapeHtml(studentName)}</title>
      <style>
        body { font-family: sans-serif; padding: 20px; }
        h2 { text-align: center; margin-bottom: 4px; }
        .subtitle { text-align: center; color: #666; font-size: 12px; margin-bottom: 16px; }
        .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; max-width: 500px; margin: 0 auto; }
        .card { text-align: center; border: 1px solid #ddd; border-radius: 8px; padding: 12px; }
        .card p { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 8px; }
        .card img { display: block; margin: 0 auto 4px; }
        .card span { font-size: 9px; color: #888; font-family: monospace; }
        @media print { body { padding: 0; } }
      </style></head><body>
      <h2>${escapeHtml(studentName)}</h2>
      <p class="subtitle">Student QR Code Card</p>
      <div class="grid">${cards}</div>
      <script>window.onload=()=>{window.print();window.close();}<\/script>
      </body></html>
    `);
    printWindow.document.close();
  }, [studentName, entries]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold flex items-center gap-1.5">
          <QrCode className="h-4 w-4" /> Student QR Codes
        </h4>
        {!rotating && (
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={handlePrint}>
            <Printer className="h-3 w-3" /> Print
          </Button>
        )}
      </div>
      {rotating && (
        <p className="text-[10px] text-muted-foreground">
          <span className="font-medium">Administration</span> na <span className="font-medium">Academic</span> QR ni za kudumu (gate &amp; session scan). Examination na Accounting hurefresh kila sekunde 30 na hufanya kazi offline.
          <br />
          <span className="font-medium">Tip:</span> Hakikisha simu yako iko kwenye <em>Auto Date &amp; Time</em> ili scanner ikukubali bila tatizo.
        </p>
      )}
      <div ref={printRef} className="grid grid-cols-2 gap-3">
        {entries.map((e) =>
          rotating && e.staticValue && e.qrType !== "admin" && e.qrType !== "academic" ? (
            <RotatingQRCard
              key={e.label}
              label={e.label}
              qrType={e.qrType}
              color={e.color}
              studentName={studentName}
            />
          ) : (
            <StaticQRCard key={e.label} label={e.label} value={e.staticValue} color={e.color} />
          ),
        )}
      </div>
    </div>
  );
}
